/**
 * User Measurement Validation Schemas
 *
 * Zod schemas for validating user measurement-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Body measurement validation with key-value pairs
 * - Progress-photo filename validation (what `POST /file/upload` returned)
 * - Input/Output type exports
 * - Validation helper functions (validate, safeValidate, isValid)
 */

import { z } from './zod';
import { MAX_PROGRESS_PHOTOS } from '../config/progress-photos.config';
import {
  getBodyMeasurementOptions,
  createEnumFromOptions,
} from '../utils/select-options';

// =============================================================================
// COMMON VALIDATION RULES
// =============================================================================

/**
 * Measurement key validation
 * Valid measurement types from body measurement options
 */
const measurementKeySchema = createEnumFromOptions(getBodyMeasurementOptions());

/**
 * Single measurement entry validation
 * Contains a measurement key and its numeric value
 */
const measurementEntrySchema = z.object({
  key: measurementKeySchema,
  value: z.number().min(0, 'Measurement value must be a positive number'),
});

/**
 * Progress-photo validation: the FILENAME `POST /file/upload` answered with,
 * not a URL. The API checks each one exists among the user's own uploads.
 */
const mediaFileNameSchema = z.string().min(1, 'Photo filename is required');

// =============================================================================
// USER MEASUREMENT VALIDATION SCHEMAS
// =============================================================================

/**
 * User measurement form validation schema
 * Used for recording body measurements with optional progress photos
 */
export const userMeasurementSchema = z.object({
  metrics: z.array(measurementEntrySchema).optional(),
  /**
   * Photos to ATTACH. On an update the API appends them to what's saved, and
   * refuses more than four in all; this checks only the four it can see.
   */
  media: z
    .array(mediaFileNameSchema)
    .max(
      MAX_PROGRESS_PHOTOS,
      `An entry can hold up to ${MAX_PROGRESS_PHOTOS} photos.`
    )
    .optional(),
});

/**
 * Partial user measurement schema for updates
 * All fields are optional for PATCH requests
 */
export const updateUserMeasurementSchema = userMeasurementSchema.partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

/** Input type for user measurement validation (before parsing) */
export type UserMeasurementInput = z.input<typeof userMeasurementSchema>;

/** Output type for user measurement validation (after parsing) */
export type UserMeasurementOutput = z.output<typeof userMeasurementSchema>;

/** Input type for partial user measurement updates (before parsing) */
export type UpdateUserMeasurementInput = z.input<
  typeof updateUserMeasurementSchema
>;

/** Output type for partial user measurement updates (after parsing) */
export type UpdateUserMeasurementOutput = z.output<
  typeof updateUserMeasurementSchema
>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate complete user measurement data
 * @param data - The data to validate
 * @returns The validated and typed user measurement
 * @throws ZodError if validation fails
 */
export function validateUserMeasurement(data: unknown): UserMeasurementOutput {
  return userMeasurementSchema.parse(data);
}

/**
 * Safely validate complete user measurement data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUserMeasurement(data: unknown) {
  return userMeasurementSchema.safeParse(data);
}

/**
 * Check if data is a valid user measurement
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUserMeasurement(data: unknown): boolean {
  return userMeasurementSchema.safeParse(data).success;
}

/**
 * Validate partial user measurement data for updates
 * @param data - The data to validate
 * @returns The validated and typed partial user measurement
 * @throws ZodError if validation fails
 */
export function validateUpdateUserMeasurement(
  data: unknown
): UpdateUserMeasurementOutput {
  return updateUserMeasurementSchema.parse(data);
}

/**
 * Safely validate partial user measurement data for updates
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateUserMeasurement(data: unknown) {
  return updateUserMeasurementSchema.safeParse(data);
}

/**
 * Check if data is a valid partial user measurement update
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateUserMeasurement(data: unknown): boolean {
  return updateUserMeasurementSchema.safeParse(data).success;
}
